import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema, generateBreadcrumbSchema } from '../../utils/seo';

const TemporaryPermitsServicePage: React.FC = () => {
  const [selectedPermitType, setSelectedPermitType] = useState<string>('movement');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Temporary Vehicle Permits Service Las Vegas Nevada - Fast DMV Permits',
    description: 'Fast temporary vehicle permit services in Las Vegas Nevada. Movement permits, trip permits, and temporary registration processed same day. Licensed Nevada DMV provider. Call 702.979.7202.',
    keywords: 'temporary permits service Las Vegas, movement permit Nevada, trip permit Las Vegas, temporary vehicle registration Nevada, DMV permits Las Vegas',
    canonical: `${siteUrl}/temporary-permits-service`,
    ogImage: `${siteUrl}/img/speedy-dmv-services.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateServiceSchema(
          'Temporary Vehicle Permits Service',
          'Fast temporary vehicle permit services in Las Vegas including movement permits, trip permits, and temporary registration.',
          '85'
        ),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' },
          { name: 'Temporary Permits Service', url: '/temporary-permits-service' }
        ])
      ]
    }
  });

  const permitTypes = {
    movement: {
      title: 'Movement Permits',
      description: 'Temporary permits for moving unregistered vehicles',
      duration: 'Up to 10 days',
      uses: ['Moving purchased vehicles', 'Transporting to inspection stations', 'One-time trips for registration']
    },
    trip: {
      title: 'Trip Permits',
      description: 'Single-trip permits for specific journeys',
      duration: '24-72 hours',
      uses: ['Emergency vehicle transport', 'Dealer test drives', 'Single destination trips']
    },
    temporary: {
      title: 'Temporary Registration',
      description: 'Short-term registration while awaiting permanent plates',
      duration: '30-90 days',
      uses: ['New vehicle purchases', 'Registration renewals in process', 'Awaiting specialty plates']
    }
  };

  const permitServices = [
    {
      icon: 'ri-truck-line',
      title: 'Vehicle Movement',
      description: 'Get permits to legally move unregistered vehicles to registration locations or inspection stations.'
    },
    {
      icon: 'ri-time-line',
      title: 'Emergency Permits',
      description: 'Fast processing for emergency situations requiring immediate vehicle transport permits.'
    },
    {
      icon: 'ri-map-line',
      title: 'Trip-Specific Permits',
      description: 'Permits for specific trips including route and destination documentation.'
    },
    {
      icon: 'ri-calendar-line',
      title: 'Extended Permits',
      description: 'Longer-term temporary permits for vehicles awaiting permanent registration completion.'
    }
  ];

  const requirements = [
    'Valid driver license or ID',
    'Proof of vehicle ownership (title or bill of sale)',
    'Nevada Evidence of Insurance',
    'Vehicle identification number (VIN)',
    'Destination and route information',
    'Specific dates of travel needed'
  ];

  const toggleExpanded = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-orange-900 via-orange-800 to-red-900 text-white py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Nevada Temporary Vehicle Permits
              </h1>
              <p className="text-xl mb-8 max-w-3xl mx-auto">
                Fast, legal temporary permits for vehicle movement and transportation. Get the permits you need 
                to legally drive unregistered vehicles in Nevada while completing registration requirements.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:702-979-7202" 
                   className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Call Now: (702) 979-7202
                </a>
                <a href="/appointment" 
                   className="bg-white text-orange-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Schedule Appointment
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Temporary Permit Services
              </h2>
              <p className="text-lg text-gray-600">
                Professional assistance for all types of temporary vehicle permits in Nevada
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {permitServices.map((service, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-md text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${service.icon} text-2xl text-orange-600`}></i>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{service.title}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Permit Types */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Types of Temporary Permits
              </h2>
              <p className="text-lg text-gray-600">
                Choose the right temporary permit for your specific situation
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              {/* Permit Type Selector */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {Object.entries(permitTypes).map(([key, permit]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedPermitType(key)}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
                      selectedPermitType === key 
                        ? 'bg-orange-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {permit.title}
                  </button>
                ))}
              </div>

              {/* Permit Type Details */}
              <div className="bg-white rounded-lg shadow-md p-8">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                      {permitTypes[selectedPermitType as keyof typeof permitTypes].title}
                    </h3>
                    <p className="text-gray-600">
                      {permitTypes[selectedPermitType as keyof typeof permitTypes].description}
                    </p>
                  </div>
                  <div className="mt-4 md:mt-0 md:ml-8">
                    <span className="inline-block bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium">
                      Duration: {permitTypes[selectedPermitType as keyof typeof permitTypes].duration}
                    </span>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Common Uses:</h4>
                  <div className="space-y-2">
                    {permitTypes[selectedPermitType as keyof typeof permitTypes].uses.map((use, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-xs text-orange-600"></i>
                        </div>
                        <span className="text-gray-700">{use}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Requirements and Process */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Requirements */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  What You'll Need
                </h2>
                <p className="text-gray-600 mb-8">
                  Bring these documents to obtain your temporary vehicle permit quickly and efficiently.
                </p>
                <div className="space-y-4">
                  {requirements.map((req, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-sm text-orange-600"></i>
                      </div>
                      <span className="text-gray-800">{req}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Process */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Quick Permit Process
                </h2>
                <div className="space-y-6">
                  {[
                    {
                      step: 1,
                      title: 'Document Review',
                      description: 'We verify your identification, vehicle ownership, and insurance documentation.',
                      time: '5 mins'
                    },
                    {
                      step: 2,
                      title: 'Permit Application',
                      description: 'Complete the Nevada temporary permit application with travel details.',
                      time: '10 mins'
                    },
                    {
                      step: 3,
                      title: 'Processing & Payment',
                      description: 'Process payment and receive your official temporary permit.',
                      time: '5 mins'
                    }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                        {item.step}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                          <span className="text-sm text-orange-600 font-medium">{item.time}</span>
                        </div>
                        <p className="text-gray-600 text-sm">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Important Information */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-alert-line text-2xl text-yellow-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Important Permit Guidelines
                  </h3>
                  <div className="space-y-4 text-gray-700">
                    <div>
                      <h4 className="font-semibold mb-2">Permit Restrictions:</h4>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Must carry permit and valid driver license while driving</li>
                        <li>Vehicle must have valid insurance coverage</li>
                        <li>Permits are for specific routes and destinations only</li>
                        <li>Cannot be used for regular daily driving</li>
                        <li>Must be used within specified time period</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Violation Consequences:</h4>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Driving without proper permits can result in citations</li>
                        <li>Vehicle may be impounded for improper permit use</li>
                        <li>Additional fees and penalties may apply</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Temporary Permit Questions
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  question: "How quickly can I get a temporary permit?",
                  answer: "Most temporary permits can be issued within 15-20 minutes with proper documentation. We process permits the same day for urgent transportation needs."
                },
                {
                  question: "Can I extend a temporary permit if I need more time?",
                  answer: "Extensions may be available depending on the permit type and circumstances. Contact us before your current permit expires to discuss extension options."
                },
                {
                  question: "What areas can I travel to with a movement permit?",
                  answer: "Movement permits typically allow travel within Nevada for specific purposes like registration or inspection. Interstate travel may require additional documentation."
                },
                {
                  question: "Do I need insurance for a temporary permit?",
                  answer: "Yes, valid Nevada insurance is required for all temporary permits. You must provide current Nevada Evidence of Insurance card."
                },
                {
                  question: "What happens if I'm stopped while using a temporary permit?",
                  answer: "You must carry the permit, your driver license, and insurance card. The permit must be used only for its stated purpose and within the specified timeframe."
                }
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md">
                  <button
                    className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none"
                    onClick={() => toggleExpanded(`faq-${index}`)}
                  >
                    <span className="font-semibold text-gray-900">{faq.question}</span>
                    <i className={`ri-arrow-${expandedSection === `faq-${index}` ? 'up' : 'down'}-s-line text-xl text-gray-500`}></i>
                  </button>
                  {expandedSection === `faq-${index}` && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 bg-orange-900 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Need a Temporary Permit Fast?
            </h2>
            <p className="text-xl mb-8">
              📞 Appointments Required – Please Call Ahead
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:702-979-7202" 
                 className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Call (702) 979-7202
              </a>
              <a href="/required-documents" 
                 className="bg-white text-orange-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Document Checklist
              </a>
            </div>
            <div className="mt-6 text-gray-300">
              <p>Speedy DMV Services - 4525 S Sandhill Rd, Las Vegas, NV</p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TemporaryPermitsServicePage;
